import React, { useMemo, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Question } from '../../models/QAndA';
import QAndAContext from '../../providers/QAndA/QAndA.context';
import { PRIORITY_ICONS } from '../../providers/QAndA/QAndA.provider';
import CircledExclamationmarkIcon from 'icons/CircledExclamationmark.icon';
import {
  capitalizeName,
  getHasElementsInArray,
  getUserFullName,
  isSingleElementArray,
} from 'utils';
import usePastTime from 'hooks/usePastTime';
import { Button } from 'ncoded-component-library';
import useMatchMedia from 'hooks/useMatchMedia';
import { generateActionKey, ManageActionKeys } from './utils';
import ShowShortenedRichText from '../ShowShortenedRichText';
import RelatedFileQa from '../RelatedFileQa';
import { NewTag } from '../NewTag';
import { QAndAMobileCard } from './components/QAndAMobileCard';
import useManageQuestion from './hooks/useManageQuestion';
import ProductsIcon from 'icons/Products.icon';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useQueryParams from 'hooks/useQueryParams';
import {
  getQuestionSubject,
  isQuestionAnswerApproved,
  isQuestionApproved,
} from '../../utils';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import FaqTag from '../FaqTag';
import { FIRST_PAGE } from 'data/global';

import './QAndASellerContainer.styles.scss';

type QAndASellerContainerProps = {
  className?: string;
};

const TABLE_COLUMNS = [
  'question',
  'subject',
  'priority',
  'expert',
  'submitted',
  'manage',
] as const;

const TRANSLATE_HEADING_CELL = [
  'question',
  'questionOnly',
  'subject',
  'expert',
  'submitted',
  'manage',
];

const SORT_COL_KEYS = {
  question: '',
  subject: 'sortSubject',
  manage: '',
  priority: 'priorityIndex',
  expert: '',
  submitted: 'submittedDate',
};
const shouldTranslateHeading = (key: string) =>
  TRANSLATE_HEADING_CELL.includes(key);

const HEADING_CELL_CONTENT: Record<
  typeof TABLE_COLUMNS[number],
  React.ReactNode
> = {
  priority: <CircledExclamationmarkIcon />,
  question: 'question',
  subject: 'subject',
  expert: 'expert',
  submitted: 'submitted',
  manage: 'manage',
};

const BODY_CONTENT_GENERATE: Record<
  typeof TABLE_COLUMNS[number],
  (
    val: Question,
    updateQuestion: (question: Partial<Question>) => void,
  ) => React.ReactNode
> = {
  subject: ({ subject, otherSubject }: Question) => (
    <div className="dr-subject-cell">
      <p>{getQuestionSubject(subject, otherSubject)}</p>
    </div>
  ),
  priority: (question: Question) => PRIORITY_ICONS[question.priority],
  question: (question, updateQuestion) => (
    <SubjectCellComponent question={question} updateQuestion={updateQuestion} />
  ),
  expert: (question: Question) => <ExpertCellComponent question={question} />,
  submitted: (question: Question) => (
    <SubmittedCellComponent question={question} />
  ),
  manage: (question: Question) => <ManageCellComponent question={question} />,
};

const QAndASellerContainer: React.FC<QAndASellerContainerProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { displayedQuestions, updateQuestion } = useContext(QAndAContext);
  const {
    params: { sort },
    setQueryParam,
  } = useQueryParams<{ sort: string; page: string }>();

  const classes = classNames('q-and-a-seller-container', className);

  const isTablet = useMatchMedia('isTablet');
  const isQuestionsListEmpty = !displayedQuestions.length;

  const tableColumns = useMemo(() => TABLE_COLUMNS, []);

  const TableHeading = useMemo(
    () => (
      <thead>
        <tr>
          {tableColumns.map((col) => {
            let content = HEADING_CELL_CONTENT[col];
            const sortingVal = SORT_COL_KEYS[col];

            if (shouldTranslateHeading(col)) {
              content = t(content as string);
            }

            const isSelected = sortingVal && sort?.includes(sortingVal);

            return (
              <th key={col} className={col}>
                <div
                  className={classNames({
                    selected: isSelected,
                    rotated: sort !== sortingVal,
                  })}
                >
                  {content}
                  {sortingVal && (
                    <Button
                      onClick={() => {
                        if (sort === sortingVal) {
                          setQueryParam('sort', `-${sortingVal}`);
                        } else {
                          setQueryParam('sort', sortingVal);
                        }
                        setQueryParam('page', FIRST_PAGE);
                      }}
                      className={classNames({
                        selected: isSelected,
                        rotated: sort === sortingVal,
                      })}
                    >
                      {isSelected ? (
                        <ArrowdownIcon />
                      ) : (
                        <>
                          <ChevrondownIcon /> <ChevrondownIcon />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
    ),
    [setQueryParam, sort, t, tableColumns],
  );

  if (isQuestionsListEmpty) return null;

  if (isTablet)
    return (
      <div className={classes}>
        {displayedQuestions.map((el) => (
          <QAndAMobileCard key={el._id} question={el} />
        ))}
      </div>
    );

  return (
    <table className={classes}>
      {TableHeading}
      <tbody>
        {displayedQuestions.map((q) => {
          const isHighlighted =
            !q.assignedUsernames.length && !isQuestionAnswerApproved(q.status);

          return (
            <tr
              key={q._id}
              className={classNames('row', { highlighted: isHighlighted })}
            >
              {tableColumns.map((col) => (
                <td key={col} className={col}>
                  {BODY_CONTENT_GENERATE[col](q, updateQuestion)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default QAndASellerContainer;

type CellProps = {
  question: Question;
  updateQuestion?: (question: Partial<Question>) => void;
};

const STATES_TO_SHOW_ANSW: Array<ManageActionKeys> = [
  'published',
  'needsApproval',
];

export const SubjectCellComponent: React.FC<CellProps> = ({
  question,
  updateQuestion,
}) => {
  const {
    subject,
    content,
    user,
    status,
    otherSubject,
    submittedDate,
    dataRoomBuySideGroup,
    isPublishedToFAQ,
    dataRoomAnswer,
    dataRoomLatestFollowupQuestion,
    dataRoomPreviousFollowupQuestion,
    dataRoomParentQuestion,
  } = question;
  const key = generateActionKey(status);

  const { t } = useTranslation();
  const isTablet = useMatchMedia('isTablet');

  const followUpQuestion =
    dataRoomLatestFollowupQuestion ||
    dataRoomPreviousFollowupQuestion ||
    dataRoomParentQuestion;

  const userName = getUserFullName(user.firstName, user.lastName);

  const groupName = dataRoomBuySideGroup.name;
  const groupHasMoreMembers =
    getHasElementsInArray(dataRoomBuySideGroup.dataRoomMemberIds) &&
    !isSingleElementArray(dataRoomBuySideGroup.dataRoomMemberIds);

  const showGroupName =
    groupHasMoreMembers || groupName?.trim() !== userName?.trim();

  return (
    <div className="subject-cell">
      {!!followUpQuestion && (
        <div className="subject-cell__follow-up">
          <span>
            {t(dataRoomLatestFollowupQuestion ? 'followUpTo' : 'followUpFrom')}
          </span>
          <ShowShortenedRichText
            question={question}
            maxHeight={60}
            asAccordion
            updateQuestion={updateQuestion}
          >
            {followUpQuestion?.content}
          </ShowShortenedRichText>
        </div>
      )}

      <div className="subject-cell__question-wrapper">
        <ShowShortenedRichText
          question={question}
          className="subject-cell__question-shortened"
          maxHeight={120}
          asAccordion
          updateQuestion={updateQuestion}
        >
          {content}
        </ShowShortenedRichText>

        {isQuestionApproved(status) && <NewTag time={submittedDate} />}
        {isPublishedToFAQ && <FaqTag />}
      </div>

      {STATES_TO_SHOW_ANSW.includes(key) && dataRoomAnswer?.content && (
        <div className="subject-cell__answer-shortened">
          <ShowShortenedRichText
            question={question}
            maxHeight={120}
            asAccordion
            updateQuestion={updateQuestion}
          >
            {question.dataRoomAnswer.content}
          </ShowShortenedRichText>
        </div>
      )}

      <div className="tags">
        {isTablet && (
          <p>
            <ProductsIcon />
            {getQuestionSubject(subject, otherSubject)}
          </p>
        )}

        <div>
          {dataRoomBuySideGroup && (
            <>
              {`${t('askedBy')} ${userName}${
                showGroupName ? ` / ${groupName}` : ''
              }`}
            </>
          )}
          <RelatedFileQa question={question} />
        </div>
      </div>
    </div>
  );
};

export const ExpertCellComponent: React.FC<CellProps> = ({ question }) => {
  const { t } = useTranslation();
  const { assignedUsernames, status } = question;

  const { isFaqAccessLoading } = useContext(QAndAContext);
  const { YourDrawer, cantManageQuestion, onManageClick } =
    useManageQuestion(question);

  const { doesntHaveAccess } = useContext(DataRoomContext);

  const cellString = useMemo(
    () =>
      assignedUsernames.reduce(
        (acc, username) =>
          `${acc}${acc ? ', ' : ''}${capitalizeName(username)}`,
        '',
      ),
    [assignedUsernames],
  );

  const unassigned = !assignedUsernames.length;

  const canAssign =
    unassigned &&
    !doesntHaveAccess &&
    !(cantManageQuestion || isFaqAccessLoading) &&
    isQuestionApproved(status);

  return (
    <>
      <div
        className={classNames({
          ununassigned: canAssign,
        })}
        onClick={() => {
          if (!canAssign) return;
          onManageClick();
        }}
      >
        {unassigned ? t('unassigned') : cellString}
      </div>
      {canAssign && YourDrawer}
    </>
  );
};

export const SubmittedCellComponent: React.FC<CellProps> = ({ question }) => {
  const { submittedDate } = question;

  const pastTime = usePastTime()(submittedDate);

  if (!pastTime) return null;

  return <div className="dr-submitted-cell">{pastTime}</div>;
};

const ManageCellComponent: React.FC<CellProps> = ({ question }) => {
  const { YourDrawer, manageButtonText, cantManageQuestion, onManageClick } =
    useManageQuestion(question);

  const { isFaqAccessLoading } = useContext(QAndAContext);
  const { doesntHaveAccess } = useContext(DataRoomContext);

  if (doesntHaveAccess) return null;

  if (cantManageQuestion || isFaqAccessLoading)
    return (
      <Button variant="link" disabled>
        {manageButtonText}
      </Button>
    );

  return (
    <>
      <Button variant="link" onClick={onManageClick}>
        {manageButtonText}
      </Button>

      {YourDrawer}
    </>
  );
};
